<template>
  <div
    :id="id"
    ref="dropdownMenu"
    class="rs-dropdown-action-menu"
  >
    <button
      :id="`${id}_button_toggle`"
      :disabled="disabled"
      class="dropdown-toggle"
      :class="{ 'dropdown-toggle--open': isVisible }"
      @click="toggleVisible"
    >
      <RsIcon
        v-if="horizontal"
        weight="fas"
        icon="ellipsis-h"
      />
      <RsIcon
        v-else
        weight="fas"
        icon="ellipsis-v"
      />
    </button>
    <div
      v-click-outside="setInvisible"
      class="rs-dropdown-action-menu__menu"
      :class="dropdownClasses"
    >
      <RsDropdownItem
        v-for="(option, i) in options"
        :id="`${id}_dropdown_item_${i}`"
        :key="option.id || i"
        :title="option.label || option.title"
        :class="option.class"
        :disabled="option.disabled"
        :tooltip="option.tooltip"
        @click="emitTo({ ...option }, {
          emitToComponent: option.emitToComponent,
          eventName: option.eventName,
        })"
      />
      <slot />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { IPropsClickConfig } from '@/shared';

export default defineComponent({
  name: 'RsDropdownActionMenu',

  props: {
    id: {
      type: String,
      default: 'dropdown-1',
    },

    visible: {
      type: Boolean,
      default: false,
    },
    /**
     * href | icon | title
     *
     */
    options: {
      type: Array as PropType<IPropsClickConfig[]>,
      default: () => [],
    },
    horizontal: {
      type: Boolean,
      default: false,
    },
    actionMenu: {
      type: Boolean,
      default: false,
    },

    /**
     * For Global Event Bus
     */
    dropdownEventName: String,
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isVisible: this.visible,
      visiblityKey: 0,
    };
  },

  computed: {
    dropdownClasses(): Record<string, boolean> {
      return {
        show: this.$data.isVisible,
        flip: ((): boolean => {
          // const scrollTop = $window.scrollTop();
          const toggleButton = document.getElementById(`${this.id}_button_toggle`);
          if (toggleButton) {
            const bottmDistance = window.innerHeight - toggleButton.getBoundingClientRect().bottom || 0;
            return bottmDistance < 120;
          }
          return false;
        })(),
      };
    },
  },
  watch: {
    visible(): void {
      if (this.isVisible && this.visible === false) {
        this.toggleVisible();
      }
    },
  },
  methods: {
    documentClick(e: Event): Vue {
      const el = this.$refs.dropdownMenu;
      const { target } = e;

      if (el !== target) this.isVisible = false;
      return this;
    },
    toggleVisible(e?: Event): Vue {
      this.isVisible = !this.isVisible;
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      this.$emit('dropdown-opened', this.$refs.dropdownMenu);
      return this;
    },
    setInvisible(): void {
      this.isVisible = false;
      this.handleDropdownChanged({ label: '', icon: '', data: this.isVisible });
    },

    handleDropdownChanged(data: IPropsClickConfig): void {
      this.$emit('dropdown-changed', { ...data });
    },
  },
});
</script>

<style lang="scss">
@import '@/styles/_variables.scss';
.rs-dropdown-action-menu {
  position: relative;
  display: inline-block;
  &__menu {
    display: none;
    position: absolute;
    top: 100%;
    left: auto;
    right: 0;
    min-width: 10rem;
    margin: 0;
    padding: 0.5rem;
    z-index: 2000;
    background-color: $white;
    box-shadow: 0 0px 10px 2px rgba(0, 0, 0, 0.1);
    border-radius: $global-radius;
    animation: slideInDown 0.25s both;
    &.show {
      display: block;
    }
    &.flip {
      top: -100%;
    }
  }
  .dropdown-toggle{
    padding: 0.5rem;
    .rs-icon{
      color: $tertiary-gray;
      transition: color 0.2s ease;
    }
    &:hover{
      .rs-icon{
        color: $tertiary-muted;
      }
    }
    &:after{
      display: none;
    }
    &:disabled {
      cursor: not-allowed;
    }
  }
}

@keyframes slideInDown {
  0% {
    transform: translateY(-1rem);
    opacity: 0;
  }
  100% {
    transform: translateY(0rem);
    opacity: 1;
  }
  0% {
    transform: translateY(-1rem);
    opacity: 0;
  }
}
@keyframes slideInUp {
  0% {
    transform: translateY(1rem);
    opacity: 0;
  }
  100% {
    transform: translateY(0rem);
    opacity: 1;
  }
  0% {
    transform: translateY(1rem);
    opacity: 0;
  }
}
</style>
